import Globe, { type GlobeInstance } from 'globe.gl';
import { MeshBasicMaterial, DoubleSide } from 'three';
import { CONFIG } from '../config.js';
import type {
  ContinentKey,
  Country,
  GeoFeature,
  GeoFeatureCollection,
} from '../types.js';
import { buildCountry, normalizeContinent } from '../data/countries.js';

const GEOJSON_PATH = './assets/data/countries.geojson';
const GLOBE_TEXTURE = '//unpkg.com/three-globe/example/img/earth-day.jpg';

export interface GlobeView {
  globe: GlobeInstance;
  features: GeoFeature[];
  countries: Country[];
  featureByIso2: Map<string, GeoFeature>;
  /** 출제·정답 강조 상태. iso2 → 칠할 색. */
  highlight: Map<string, string>;
  /** highlight 변경 후 호출하면 polygonCapColor가 다시 평가됨 */
  refresh: () => void;
  /** 카메라를 해당 국가로 이동 */
  flyTo: (iso2: string, durationMs?: number) => void;
  /** 카메라를 여러 국가의 centroid 평균으로 이동 + 줌아웃 (위치 모드용) */
  flyToMany: (iso2List: readonly string[], durationMs?: number) => void;
  /** 지구본 자동 회전 on/off */
  setAutoRotate: (on: boolean) => void;
  /** 출제 중인 국가는 hover 라벨에서 이름을 가린다. null이면 해제. */
  setHiddenLabel: (iso2: string | null) => void;
  /** 모든 폴리곤의 hover 라벨을 통째로 가린다 (위치 모드 출제 중). */
  setAllLabelsHidden: (on: boolean) => void;
  /** 사용자가 지구본 위 폴리곤을 클릭했을 때 호출할 핸들러를 등록. null이면 비활성. */
  setPolygonClick: (handler: ((iso2: string) => void) | null) => void;
}

interface PolygonDatum {
  feature: GeoFeature;
  iso2: string;
}

export async function createGlobe(container: HTMLElement): Promise<GlobeView> {
  const fc = await loadGeoJson();
  const features = fc.features.filter((f) => {
    const iso = f.properties.ISO_A2;
    return typeof iso === 'string' && iso.length === 2 && iso !== '-99';
  });

  const countries: Country[] = [];
  const featureByIso2 = new Map<string, GeoFeature>();
  for (const f of features) {
    const cont = normalizeContinent(f.properties.CONTINENT);
    if (cont === null) continue;
    const iso2 = f.properties.ISO_A2;
    const iso3 = f.properties.ISO_A3;
    countries.push(buildCountry(iso2, iso3, cont satisfies ContinentKey));
    featureByIso2.set(iso2, f);
  }

  const data: PolygonDatum[] = [];
  featureByIso2.forEach((feature, iso2) => {
    data.push({ feature, iso2 });
  });

  // 국경선용 path 데이터 — 각 폴리곤 ring을 [lat, lng] 시퀀스로 변환.
  // globe.gl의 polygonStrokeColor는 1px 라인이라 텍스처 위에서 잘 안 보이므로
  // pathsData로 굵은 라인을 추가로 그린다.
  type LL = [number, number];
  const borderPaths: LL[][] = [];
  const collectRings = (coords: unknown): void => {
    if (!Array.isArray(coords)) return;
    const first = coords[0];
    if (
      Array.isArray(first) &&
      typeof (first as unknown[])[0] === 'number' &&
      typeof (first as unknown[])[1] === 'number'
    ) {
      // ring: [[lng, lat], ...]
      borderPaths.push(
        (coords as number[][]).map(([lng, lat]) => [lat ?? 0, lng ?? 0] as LL),
      );
      return;
    }
    for (const c of coords) collectRings(c);
  };
  for (const f of features) collectRings(f.geometry.coordinates);

  const highlight = new Map<string, string>();
  let hiddenLabelIso2: string | null = null;
  let allLabelsHidden = false;
  let polygonClickHandler: ((iso2: string) => void) | null = null;
  const { colors, highlightAltitude } = CONFIG;

  // 평지 국가는 지구본 텍스처가 그대로 보이도록 cap/side를 숨김 — 국경선(stroke)만 남김.
  // 하이라이트된 국가는 솟아오르고 반투명하게 칠해 아래 텍스처가 비쳐 보이도록 한다
  // (cap은 살짝 투명, 옆면은 많이 투명).
  //
  // 모바일 성능: 문제 바뀔 때 refresh()가 모든 폴리곤(~200개)의 material accessor를
  // 재평가한다. 매번 `new MeshBasicMaterial(...)`를 돌리면 200여 개 객체 할당 +
  // 쉐이더 바인딩 셋업이 카메라 fly 시작 직전에 터져 끊김의 주범.
  // → 안 보이는 material은 단일 인스턴스, 보이는 것은 색별로 캐시해 재사용.
  const invisibleCapMaterial = new MeshBasicMaterial({ visible: false });
  const invisibleSideMaterial = new MeshBasicMaterial({ visible: false });
  const capMaterialByColor = new Map<string, MeshBasicMaterial>();
  const sideMaterialByColor = new Map<string, MeshBasicMaterial>();
  const getCapMaterial = (color: string): MeshBasicMaterial => {
    let m = capMaterialByColor.get(color);
    if (m === undefined) {
      m = new MeshBasicMaterial({
        color,
        side: DoubleSide,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
      });
      capMaterialByColor.set(color, m);
    }
    return m;
  };
  const getSideMaterial = (color: string): MeshBasicMaterial => {
    let m = sideMaterialByColor.get(color);
    if (m === undefined) {
      m = new MeshBasicMaterial({
        color,
        side: DoubleSide,
        transparent: true,
        opacity: 0.25,
        depthWrite: false,
      });
      sideMaterialByColor.set(color, m);
    }
    return m;
  };
  const makeCapMaterial = (d: object): MeshBasicMaterial => {
    const iso2 = (d as PolygonDatum).iso2;
    if (!highlight.has(iso2)) return invisibleCapMaterial;
    const color = highlight.get(iso2) ?? colors.highlight;
    return getCapMaterial(color);
  };
  const makeSideMaterial = (d: object): MeshBasicMaterial => {
    const iso2 = (d as PolygonDatum).iso2;
    if (!highlight.has(iso2)) return invisibleSideMaterial;
    const color = highlight.get(iso2) ?? colors.highlight;
    return getSideMaterial(color);
  };

  const globe = new Globe(container, { animateIn: false })
    .globeImageUrl(GLOBE_TEXTURE)
    .backgroundColor('#0a1530')
    .atmosphereColor('#a5d8ff')
    .atmosphereAltitude(0.22)
    .polygonsData(data as unknown as object[])
    .polygonGeoJsonGeometry(
      (d) => (d as PolygonDatum).feature.geometry as never,
    )
    .polygonAltitude((d) => {
      const iso2 = (d as PolygonDatum).iso2;
      return highlight.has(iso2) ? highlightAltitude : 0.008;
    })
    .polygonCapMaterial(makeCapMaterial)
    .polygonSideMaterial(makeSideMaterial)
    .polygonStrokeColor(() => null as unknown as string)
    .polygonsTransitionDuration(400)
    .polygonLabel((d) => {
      if (allLabelsHidden) return '';
      const iso2 = (d as PolygonDatum).iso2;
      if (iso2 === hiddenLabelIso2) return '❓';
      const country = countries.find((c) => c.iso2 === iso2);
      return country ? country.nameKo : iso2;
    })
    .onPolygonClick((datum) => {
      if (polygonClickHandler === null) return;
      const iso2 = (datum as PolygonDatum).iso2;
      if (iso2) polygonClickHandler(iso2);
    })
    .pathsData(borderPaths as unknown as object[])
    .pathPoints((d) => d as object[])
    .pathPointLat((p) => (p as LL)[0])
    .pathPointLng((p) => (p as LL)[1])
    .pathColor(() => colors.baseStroke)
    .pathStroke(0.35)
    .pathTransitionDuration(0);

  resize(globe, container);
  window.addEventListener('resize', () => resize(globe, container));

  // 자동 회전 (globe.gl controls).
  const controls = globe.controls();
  controls.autoRotate = true;
  controls.autoRotateSpeed = CONFIG.globeRotateSpeed * 0.1;
  controls.enableDamping = true;
  controls.dampingFactor = 0.1;

  globe.pointOfView({ lat: 20, lng: 0, altitude: 2.2 }, 0);

  const refresh = () => {
    // material/altitude accessor를 다시 평가하도록 재설정
    globe
      .polygonCapMaterial(makeCapMaterial)
      .polygonSideMaterial(makeSideMaterial)
      .polygonAltitude(globe.polygonAltitude());
  };

  const flyTo = (iso2: string, durationMs = 1200) => {
    const feature = featureByIso2.get(iso2);
    if (!feature) return;
    const center = geoCentroid(feature);
    if (!center) return;
    globe.pointOfView(
      { lat: center.lat, lng: center.lng, altitude: 1.3 },
      durationMs,
    );
  };

  const flyToMany = (isoList: readonly string[], durationMs = 1200) => {
    if (isoList.length === 0) return;
    if (isoList.length === 1) {
      flyTo(isoList[0]!, durationMs);
      return;
    }
    let lngSum = 0;
    let latSum = 0;
    let count = 0;
    for (const iso of isoList) {
      const feature = featureByIso2.get(iso);
      if (!feature) continue;
      const c = geoCentroid(feature);
      if (!c) continue;
      lngSum += c.lng;
      latSum += c.lat;
      count += 1;
    }
    if (count === 0) return;
    // 보기 N개가 한 화면에 들어오도록 줌아웃. altitude는 일단 고정값으로 시작.
    globe.pointOfView(
      { lat: latSum / count, lng: lngSum / count, altitude: 2.0 },
      durationMs,
    );
  };

  const setAutoRotate = (on: boolean) => {
    controls.autoRotate = on;
  };

  const setHiddenLabel = (iso2: string | null) => {
    hiddenLabelIso2 = iso2;
  };

  const setAllLabelsHidden = (on: boolean) => {
    allLabelsHidden = on;
  };

  const setPolygonClick = (handler: ((iso2: string) => void) | null) => {
    polygonClickHandler = handler;
  };

  return {
    globe,
    features,
    countries,
    featureByIso2,
    highlight,
    refresh,
    flyTo,
    flyToMany,
    setAutoRotate,
    setHiddenLabel,
    setAllLabelsHidden,
    setPolygonClick,
  };
}

async function loadGeoJson(): Promise<GeoFeatureCollection> {
  const res = await fetch(GEOJSON_PATH);
  if (!res.ok) {
    throw new Error(`GeoJSON 로드 실패: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as GeoFeatureCollection;
}

function resize(globe: GlobeInstance, container: HTMLElement): void {
  globe.width(container.clientWidth).height(container.clientHeight);
}

/**
 * GeoJSON Feature의 대략적인 중심점 (모든 좌표의 평균).
 * 정밀한 무게중심은 아니지만 카메라 이동 용도로는 충분.
 */
function geoCentroid(feature: GeoFeature): { lat: number; lng: number } | null {
  let lngSum = 0;
  let latSum = 0;
  let count = 0;
  const geom = feature.geometry;
  const walk = (coords: unknown): void => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      lngSum += coords[0];
      latSum += coords[1];
      count += 1;
      return;
    }
    for (const c of coords) walk(c);
  };
  walk(geom.coordinates);
  if (count === 0) return null;
  return { lng: lngSum / count, lat: latSum / count };
}
